import { Alert, Button, Card, CardActions, CardContent, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import QUERIES from 'modules/common/queries';

import CardTitleHeader from 'components/v2/card/CardTitleHeader';
import { useLocalization } from 'localization';
import { ordersQueryKey } from 'api/orders/query';
import LinkButton from 'components/v2/button/LinkButton';
import routes from 'app.routes';
import ListCheckIcon from 'components/v2/icons/ListCheckIcon';
import RefreshButton from 'components/v2/button/RefreshButton';
import { useTasks } from 'modules/tasks/hooks';
import useOpenTasksModal from 'hooks/services/useOpenTasksModal';
import { useTableHeaderSorting } from 'hooks/general/useTableSorting';
import { TaskStatus } from 'api';

const DashboardTasks = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const openTask = useOpenTasksModal();
  const { sortHeader } = useTableHeaderSorting({ sortOrder: 'createdAt', sortDirection: 'desc' });

  const {
    data: taskData,
    isLoading: isTasksDataLoading,
    isError,
    isFetching: isTasksDataFetching,
  } = useTasks(
    {
      pageSize: 50,
      orderBy: sortHeader?.sortOrder as any,
      orderByDirection: sortHeader?.sortDirection as any,
      status: [TaskStatus.Partial, TaskStatus.Open],
    },
    true
  );

  const tasks = taskData?.pages.flatMap(page => page.items) || [];
  const [taskIndex, setTaskIndex] = useState(0);

  const handleViewTasks = () => {
    openTask(tasks[taskIndex]);
  };

  const handleViewNextTask = () => {
    if (taskIndex < tasks.length) setTaskIndex(prev => ++prev);
  };

  const renderComponent = () => {
    if (isTasksDataLoading || isTasksDataFetching) {
      return (
        <Stack>
          <Skeleton animation="wave" width="100%" height="20rem" />
        </Stack>
      );
    }

    if (isError) {
      return (
        <Alert variant="outlined" severity="error">
          {t('common.messages.fetchingError')}
        </Alert>
      );
    }

    return (
      <Stack sx={{ position: 'relative' }}>
        {/* Bottom card (third layer) */}
        {tasks?.length ? (
          <Card
            sx={{
              position: 'absolute',
              width: '96%',
              height: '100%',
              top: '1rem',
              borderRadius: '1rem',
              right: '1.75%',
              zIndex: 1,
            }}
          />
        ) : null}
        {/* Middle card (second layer) */}
        {tasks?.length ? (
          <Card
            sx={{
              position: 'absolute',
              width: '98%',
              height: '100%',
              top: '0.5rem',
              right: '1%',
              borderRadius: '1rem',
              zIndex: 2,
            }}
          />
        ) : null}
        {/* Upper card (first layer) */}
        <Card sx={{ zIndex: 3 }}>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 0 0',
              alignSelf: 'stretch',
            }}
          >
            {tasks?.length ? (
              <Card
                sx={{
                  height: 'auto',
                  border: 'none',
                }}
              >
                <Stack gap={3} sx={{ padding: { xs: '1.5rem', md: '2rem' } }}>
                  <Stack gap="1rem" justifyContent="center" alignItems="center">
                    <Stack flex={1} gap={1} justifyContent="center" alignItems="center">
                      <Typography variant="h5">{t(`tasks.categories.${tasks?.[taskIndex]?.type}`)}</Typography>
                      <Typography variant="body2" textAlign="center">
                        {t('common.sentences.taskReminder')}: {tasks?.[taskIndex]?.caseReference ?? ''}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" gap="0.625rem" justifyContent="center" alignItems="center">
                    <Button
                      sx={{ flex: 1 }}
                      disabled={!tasks || taskIndex >= tasks.length - 1}
                      onClick={handleViewNextTask}
                      variant="outlined"
                    >
                      {t('common.terms.later')}
                    </Button>
                    <Button sx={{ flex: 1 }} variant={'contained'} onClick={handleViewTasks}>
                      {t('common.actions.openTask')}
                    </Button>
                  </Stack>
                </Stack>
              </Card>
            ) : (
              <Typography variant="body2" sx={{ pt: 2.5 }} alignSelf="center">
                {t('common.actions.noTasks')}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>
    );
  };

  return (
    <Card
      sx={{
        width: '100%',
        minHeight: '30rem',
        justifyContent: 'space-between',
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: '2rem' }}>
          <CardTitleHeader title="common.actions.openTasks" icon={<ListCheckIcon />} />
          {isError && <RefreshButton queryKey={() => [[QUERIES.tasks], ordersQueryKey.countOpenTasks()]} />}
        </Stack>
      </CardContent>
      {renderComponent()}
      {tasks?.length > 0 && (
        <CardActions
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <LinkButton
            size="medium"
            onClick={() => navigate(routes.v2.tasks.overview)}
            label={t('common.messages.showOpenTasks')}
          />
        </CardActions>
      )}
    </Card>
  );
};

export default DashboardTasks;
